import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaUserGraduate, FaEnvelope, FaPhone, FaMapMarkerAlt, FaInstagram, FaEye, FaCalendarDay } from 'react-icons/fa';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../contexts/LanguageContext';

const Footer = () => {
  const { t } = useLanguage();
  const [totalVisits, setTotalVisits] = useState(0);
  const [todayVisits, setTodayVisits] = useState(0);

  useEffect(() => {
    // Record visit once per session
    const recordVisit = async () => {
      let sessionId = sessionStorage.getItem('visit_session');
      if (!sessionId) {
        sessionId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        sessionStorage.setItem('visit_session', sessionId);
        await supabase.from('site_visits').insert({ session_id: sessionId });
      }
    };

    // Fetch stats
    const fetchStats = async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString();

      const [totalRes, todayRes] = await Promise.all([
        supabase.from('site_visits').select('id', { count: 'exact', head: true }),
        supabase.from('site_visits').select('id', { count: 'exact', head: true }).gte('created_at', todayISO),
      ]);

      setTotalVisits(totalRes.count || 0);
      setTodayVisits(todayRes.count || 0);
    };

    recordVisit().then(fetchStats);
  }, []);

  const stats = [
    { icon: <FaEye className="text-gold-400" />, value: totalVisits, label: t('footer.totalVisits') },
    { icon: <FaCalendarDay className="text-gold-400" />, value: todayVisits, label: t('footer.today') },
  ];

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-gray-950 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-12">

        {/* ── Visitor Stats Bar ── */}
        <div className="mb-10 pb-8 border-b border-gray-800">
          <div className="flex items-center justify-center gap-6 sm:gap-10">
            {stats.map((s, i) => (
              <div key={i} className="text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  {s.icon}
                  <span className="text-xl sm:text-2xl font-bold text-white">{s.value.toLocaleString('id-ID')}</span>
                </div>
                <span className="text-[11px] sm:text-xs text-gray-500 uppercase tracking-wider">{s.label}</span>
              </div>
            ))}
          </div>

        </div>

        {/* ── Main Grid ── */}
        {/* Mobile: Brand only | Desktop: Full 4-column */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">

          {/* Brand — always visible */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <FaUserGraduate className="text-gold-400 text-2xl" />
              <span className="text-white font-bold text-xl">
                PIMNAS <span className="text-gold-400">Alumni</span>
              </span>
            </div>
            <p className="text-sm leading-relaxed text-gray-400">
              {t('footer.desc')}
            </p>
            {/* Mobile-only social link */}
            <div className="flex gap-3 mt-4 lg:hidden">
              <a href="https://www.instagram.com/official.alumnipimnas/" target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 bg-gray-800 hover:bg-pink-600 rounded-lg flex items-center justify-center transition-colors">
                <FaInstagram />
              </a>
            </div>
          </div>

          {/* Quick Links — hidden on mobile */}
          <div className="hidden lg:block">
            <h4 className="text-white font-semibold mb-4">{t('footer.navigation')}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="hover:text-gold-400 transition-colors">{t('footer.home')}</Link></li>
              <li><Link to="/alumni" className="hover:text-gold-400 transition-colors">{t('footer.directory')}</Link></li>
              <li><Link to="/berita" className="hover:text-gold-400 transition-colors">{t('footer.newsArticles')}</Link></li>
              <li><Link to="/statistik" className="hover:text-gold-400 transition-colors">{t('footer.statistics')}</Link></li>
              <li><Link to="/tentang" className="hover:text-gold-400 transition-colors">{t('footer.aboutUs')}</Link></li>
            </ul>
          </div>

          {/* Contact — always visible */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer.contactUs')}</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2">
                <FaEnvelope className="text-gold-400" />
                <span>informasialumnipimnas@gmail.com</span>
              </li>
              <li className="flex items-center gap-2">
                <FaPhone className="text-gold-400" />
                <span>+62 881 02781 8897</span>
              </li>
              <li className="flex items-start gap-2">
                <FaMapMarkerAlt className="text-gold-400 mt-0.5" />
                <span>Jakarta, Indonesia</span>
              </li>
            </ul>
          </div>

          {/* Social — hidden on mobile (compact version shown in Brand section) */}
          <div className="hidden lg:block">
            <h4 className="text-white font-semibold mb-4">{t('footer.socialMedia')}</h4>
            <div className="flex gap-3">
              <a href="https://www.instagram.com/official.alumnipimnas/" target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-pink-600 rounded-lg flex items-center justify-center transition-colors">
                <FaInstagram />
              </a>
            </div>
          </div>
        </div>

        {/* ── Copyright Bar ── */}
        <div className="border-t border-gray-800 mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between text-xs text-gray-500 gap-2">
          <p>&copy; {new Date().getFullYear()} {t('footer.copyright')}</p>
          <p>{t('footer.madeWith')}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
